import { useGetMerchantAppProductsQuery } from "Api/Queries/Merchants/useGetMerchantAppProductsQuery";
import { useGetMerchantQuery } from "Api/Queries/Merchants/useGetMerchantQuery";
import { useMerchantAppProductsPricings } from "Components/Hunter/Merchants/CompleteRegistration/hooks/useMerchantAppProductsPricings";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { FormSection } from "Components/Shared/FormSection";
import { AppProductsContent } from "Components/Shared/Merchant/Products/AppProducts/AppProductsContent";
import { AppProductsSkeleton } from "Components/Shared/Merchant/Products/AppProducts/AppProductsSkeleton";
import type { GetPrerequisites } from "Components/Shared/Merchant/Products/AppProducts/types";
import type { ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Components.Merchant.AppProducts;

export type AppProductsVariant = "editable" | "readonly" | "activatable";

type Props = {
  merchantPublicID: string;
  variant?: AppProductsVariant;
  getPrerequisites?: GetPrerequisites;
  isOnboarding?: boolean;
};

export const AppProducts = ({
  merchantPublicID,
  variant = "readonly",
  getPrerequisites,
  isOnboarding,
}: Props) => {
  const { t } = useResource();
  const {
    data: products,
    isLoading: isProductsLoading,
    isError: isProductsError,
  } = useGetMerchantAppProductsQuery(merchantPublicID);

  const {
    data: merchantData,
    isLoading: isMerchantLoading,
    isError: isMerchantError,
  } = useGetMerchantQuery(merchantPublicID);

  const {
    isLoading: isPricingsLoading,
    isError: isPricingsError,
    allGroupedPricings,
  } = useMerchantAppProductsPricings(merchantPublicID);

  const isLoading = isProductsLoading || isMerchantLoading || isPricingsLoading;
  const isError = isProductsError || isMerchantError || isPricingsError;

  if (isLoading) {
    return (
      <Layout>
        <AppProductsSkeleton />
      </Layout>
    );
  }

  if (isError || !products || !merchantData || !allGroupedPricings) {
    return (
      <Layout>
        <ErrorText>{t(COMPONENT_RESOURCES.LoadingError)}</ErrorText>
      </Layout>
    );
  }

  return (
    <Layout>
      <AppProductsContent
        products={products}
        merchantData={merchantData}
        allGroupedPricings={allGroupedPricings}
        variant={variant}
        merchantPublicID={merchantPublicID}
        getPrerequisites={getPrerequisites}
        isOnboarding={isOnboarding}
      />
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  return (
    <FormSection legend={t(COMPONENT_RESOURCES.Title)}>{children}</FormSection>
  );
};
